import React, { useEffect } from "react";
import { format } from "date-fns";
import {
  BellIcon,
  CheckIcon,
  ArrowPathIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { useNotifications } from "../../contexts/NotificationContext";
import { useLocation } from "react-router-dom";

// Helper function to format timestamp
const formatDate = (timestamp: number) => {
  return format(new Date(timestamp), "MMM d, yyyy h:mm a");
};

const Notifications: React.FC = () => {
  const location = useLocation();
  const {
    notifications,
    loading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    isRead,
    refresh,
  } = useNotifications();

  // Refresh notifications when the page is visited
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Mark as read when notification is viewed
  useEffect(() => {
    notifications.forEach((notification) => {
      if (!isRead(notification.id)) {
        markAsRead(notification.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const handleRefresh = () => {
    refresh();
  };

  return (
    <div className="container mx-auto px-4">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Notifications</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Refresh notifications"
          >
            <ArrowPathIcon
              className={`h-5 w-5 text-gray-600 ${
                loading ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>
      </div>
      <div className="flex justify-start mb-4 mt-2">
        <button
          onClick={markAllAsRead}
          disabled={notifications.length === 0}
          className="flex items-center px-4 py-1 border border-gray-200 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Mark All as Read
        </button>
      </div>

      {loading && notifications.length === 0 ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="text-center py-8">
            <BellIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Notifications
            </h3>
            <p className="text-gray-500">
              You don't have any notifications yet.
            </p>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <ul className="divide-y divide-gray-200">
            {notifications.map((notification, index) => (
              <li
                key={notification.id}
                className={`px-4 py-4 rounded-lg shadow-md overflow-hidden mb-2 ${
                  !isRead(notification.id) ? "bg-goldlight" : " bg-white"
                } transition-colors`}
              >
                <div className="flex items-start">
                  {!isRead(notification.id) && (
                    <div className="mr-3 flex-shrink-0">
                      <SparklesIcon className="h-5 w-5 text-blue-500" />
                    </div>
                  )}
                  <div className={`${!isRead(notification.id) ? "" : ""}`}>
                    <h4
                      className={`text-sm ${
                        isRead(notification.id) ? "font-bold" : "font-bold"
                      }`}
                    >
                      {notification.title}
                    </h4>
                    <p className="mt-1 text-sm text-gray-700">
                      {notification.message}
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                      {formatDate(notification.createdAt)}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {hasMore && (
            <div className="flex justify-center p-4">
              <button
                onClick={() => loadMore()}
                disabled={loading}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
                    Loading...
                  </>
                ) : (
                  "Load More..."
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Notifications;
